// src/context/ChecklistDefaultsContext.js
import React, { createContext, useContext, useMemo } from "react";
import { useSeasons } from "./SeasonContext";
import { useAlerts } from "./AlertsContext";
import {
  getHazardId,
  isHazardVisible,
  normalizeHazardId,
} from "../config/hazardConfig";

const ChecklistDefaultsContext = createContext(null);

const normalizeAlertType = (type) => {
  if (!type || typeof type !== "string") return null;
  const t = type.trim().toLowerCase();

  if (t === "red flag warning") return "Warning";
  if (t === "fire weather watch") return "Watch";
  if (t === "burn ban") return "Burn Ban";
  if (t === "evacuation") return "Evacuation";
  if (t === "seasonal") return "Seasonal";
  if (t === "warning") return "Warning";
  if (t === "watch") return "Watch";
  if (t === "advisory") return "Advisory";
  if (t === "drought") return "Advisory";

  return type;
};

const severityRank = (type) => {
  const normalized = normalizeAlertType(type);
  if (!normalized) return 0;

  if (normalized === "Evacuation") return 4;
  if (normalized === "Warning") return 3;
  if (normalized === "Watch") return 2;
  if (normalized === "Advisory") return 2;
  if (normalized === "Burn Ban") return 1;
  return 0;
};

const pushContext = (list, ctx) => {
  if (!ctx?.hazardId) return;

  const normalizedId = normalizeHazardId(ctx.hazardId);
  if (!normalizedId) return;
  if (!isHazardVisible(normalizedId)) return;

  list.push({
    ...ctx,
    hazardId: normalizedId,
    alertType: normalizeAlertType(ctx.alertType),
  });
};

const dedupeExactContexts = (contexts) => {
  const seen = new Map();

  contexts.forEach((ctx) => {
    const key = [
      normalizeHazardId(ctx.hazardId),
      String(normalizeAlertType(ctx.alertType) || "").toLowerCase(),
      String(ctx.source || "").toLowerCase(),
      String(ctx.sourceId || "").toLowerCase(),
      Number(ctx.priority || 0),
    ].join("__");

    if (!seen.has(key)) {
      seen.set(key, ctx);
    }
  });

  return Array.from(seen.values()).sort((a, b) => {
    const rankDiff = Number(b.priority || 0) - Number(a.priority || 0);
    if (rankDiff !== 0) return rankDiff;
    return String(a.label || "").localeCompare(String(b.label || ""));
  });
};

const dedupeContextsByHazardForPrimary = (contexts) => {
  const bestByHazard = new Map();

  contexts.forEach((ctx) => {
    const hazardId = normalizeHazardId(ctx.hazardId);
    if (!hazardId) return;

    const prev = bestByHazard.get(hazardId);

    if (!prev || (ctx.priority || 0) > (prev.priority || 0)) {
      bestByHazard.set(hazardId, ctx);
      return;
    }

    if (
      prev &&
      (ctx.priority || 0) === (prev.priority || 0) &&
      prev.source === "season" &&
      ctx.source === "alert"
    ) {
      bestByHazard.set(hazardId, ctx);
    }
  });

  return Array.from(bestByHazard.values()).sort((a, b) => {
    const rankDiff = Number(b.priority || 0) - Number(a.priority || 0);
    if (rankDiff !== 0) return rankDiff;
    return String(a.label || "").localeCompare(String(b.label || ""));
  });
};

const buildAlertContextsFromResolvedAlerts = (
  resolvedWeatherAlerts,
  alerts,
  flags,
) => {
  const contexts = [];

  (resolvedWeatherAlerts || []).forEach((alert) => {
    const resolved = alert?.resolved;
    if (!resolved?.hazardLabel || !resolved?.hazardId) return;
    if (!resolved.checklistEligible) return;

    const hazardId = getHazardId(resolved.hazardLabel) || resolved.hazardId;
    const alertType = normalizeAlertType(resolved.alertType);
    const priority = severityRank(alertType);

    pushContext(contexts, {
      hazardId,
      label: resolved.hazardLabel,
      source: "alert",
      sourceId: alert.id,
      alertType,
      priority,
    });
  });

  (alerts || [])
    .filter((a) => a.type === "burnBan")
    .forEach((alert) => {
      pushContext(contexts, {
        hazardId: getHazardId("Wildfire"),
        label: "Wildfire",
        source: "alert",
        sourceId: alert.id,
        alertType: "Burn Ban",
        priority: severityRank("Burn Ban"),
      });
    });

  if (flags?.hasDrought) {
    pushContext(contexts, {
      hazardId: getHazardId("Drought") || "drought",
      label: "Drought",
      source: "alert",
      sourceId: "drought-usdm",
      alertType: "Advisory",
      priority: severityRank("Advisory"),
    });
  }

  return dedupeExactContexts(contexts);
};

const buildSituationCandidates = (alertContexts) => {
  return (alertContexts || [])
    .map((ctx) => ({
      hazard: ctx.label,
      alertType: ctx.alertType,
      rank: severityRank(ctx.alertType),
      source: ctx.source,
      sourceId: ctx.sourceId,
    }))
    .filter((c) => isHazardVisible(c.hazard));
};

const buildPlanningContexts = ({ currentSeasons, alertContexts }) => {
  const contexts = [];

  (currentSeasons || []).forEach((season) => {
    if (!season?.hazardKey) return;

    const hazardId = getHazardId(season.hazardKey);

    pushContext(contexts, {
      hazardId,
      label: season.hazardKey,
      source: "season",
      alertType: "Seasonal",
      priority: 0,
    });
  });

  (alertContexts || []).forEach((ctx) => {
    pushContext(contexts, ctx);
  });

  const activeContexts = dedupeExactContexts(contexts);
  const activeHazardIds = Array.from(
    new Set(activeContexts.map((c) => c.hazardId).filter(Boolean)),
  );

  const primaryContexts = dedupeContextsByHazardForPrimary(contexts);

  return {
    activeContexts,
    activeHazardIds,
    primaryContext: primaryContexts[0] || null,
  };
};

export const ChecklistDefaultsProvider = ({ children }) => {
  const { currentSeasons, defaultSeason } = useSeasons();
  const { flagsReady, flags, alerts, resolvedWeatherAlerts } = useAlerts();

  const value = useMemo(() => {
    let seasonalHazard = "Routine";

    if (defaultSeason?.id === "winterWeather") {
      seasonalHazard = "Winter Weather";
    } else if (defaultSeason?.id === "wildfire") {
      seasonalHazard = "Wildfire";
    } else {
      if (currentSeasons.some((s) => s.id === "wildfire")) {
        seasonalHazard = "Wildfire";
      } else if (currentSeasons.some((s) => s.id === "winterWeather")) {
        seasonalHazard = "Winter Weather";
      }
    }

    let defaultHazard = seasonalHazard;
    let defaultAlertType = "Seasonal";

    const alertContexts = buildAlertContextsFromResolvedAlerts(
      resolvedWeatherAlerts,
      alerts,
      flags,
    );

    const { activeContexts, activeHazardIds, primaryContext } =
      buildPlanningContexts({
        currentSeasons,
        alertContexts,
      });

    let candidates = [];
    let hasSpecificAlertDefaults = false;

    if (!flagsReady) {
      return {
        defaultHazard,
        defaultAlertType,
        seasonalHazard,
        candidates,
        activeContexts,
        activeHazardIds,
        primaryContext,
        hasSpecificAlertDefaults,
      };
    }

    const hasAnyAlerts =
      (alerts && alerts.length > 0) || Boolean(flags?.hasDrought);

    if (!hasAnyAlerts) {
      return {
        defaultHazard,
        defaultAlertType,
        seasonalHazard,
        candidates,
        activeContexts,
        activeHazardIds,
        primaryContext,
        hasSpecificAlertDefaults,
      };
    }

    candidates = buildSituationCandidates(alertContexts);

    if (!candidates.length) {
      return {
        defaultHazard,
        defaultAlertType,
        seasonalHazard,
        candidates,
        activeContexts,
        activeHazardIds,
        primaryContext,
        hasSpecificAlertDefaults,
      };
    }

    candidates.sort((a, b) => b.rank - a.rank);
    const best = candidates[0];

    defaultHazard = best.hazard;
    defaultAlertType = best.alertType;
    hasSpecificAlertDefaults = true;

    return {
      defaultHazard,
      defaultAlertType,
      seasonalHazard,
      candidates,
      activeContexts,
      activeHazardIds,
      primaryContext,
      hasSpecificAlertDefaults,
    };
  }, [
    currentSeasons,
    defaultSeason,
    flagsReady,
    flags,
    alerts,
    resolvedWeatherAlerts,
  ]);

  return (
    <ChecklistDefaultsContext.Provider value={value}>
      {children}
    </ChecklistDefaultsContext.Provider>
  );
};

export const useChecklistDefaults = () => {
  const ctx = useContext(ChecklistDefaultsContext);
  if (!ctx) {
    throw new Error(
      "useChecklistDefaults must be used within ChecklistDefaultsProvider",
    );
  }
  return ctx;
};